// @ts-nocheck
import * as React from 'react';
import {
  Sheet,
  SheetContent,
} from '@frontend/shadcn/components/ui/sheet';
import { Button } from '@revfluence/fresh';
import { CircleInfoIcon, ArrowUpFromLineIcon } from '@revfluence/fresh-icons/regular/esm';
import { Avatar } from '@revfluence/fresh';
import { useGetCustomStageTaskDetails } from '@frontend/app/hooks';
import { useUpdateCustomStageTaskDetails } from '@frontend/app/hooks/useUpdateCustomStageTaskDetails';
import { FieldSchemaType } from '@frontend/app/types/globalTypes';
import { NameCell } from '@frontend/app/refresh-components';
import { useState } from 'react';
import { SingleSelect } from './SingleSelect';
import { MultiSelect } from './MultiSelect';

interface CustomStageFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  member: {
    name: string;
    profilePicture?: string;
  };
  stageId: number;
  programId: number;
  memberId: number;
  refetchData?: () => Promise<void>;
}

export const CustomStageFormDrawer: React.FC<CustomStageFormDrawerProps> = ({
  isOpen,
  onClose,
  member,
  stageId,
  programId,
  memberId,
  refetchData,
}) => {
  const { stageTaskDetails, loading } = useGetCustomStageTaskDetails({
    programId,
    stageId,
    memberId,
  });

  const { updateStageTaskDetails, loading: updateLoading, error: updateError } = useUpdateCustomStageTaskDetails();

  const [formValues, setFormValues] = useState<Record<string, any>>({});

  // Initialize form values from API response
  React.useEffect(() => {
    if (stageTaskDetails?.fieldSchemas) {
      const initialValues: Record<string, any> = {};
      stageTaskDetails.fieldSchemas.forEach((schema) => {
        if (!schema || schema.type === FieldSchemaType.UPLOAD) return;

        const fieldValue = schema.fieldValues?.[0];
        if (schema.type === FieldSchemaType.MULTI_SELECT) {
          initialValues[schema.id] = fieldValue?.metadata?.value || [];
        } else if (schema.type === FieldSchemaType.SINGLE_SELECT) {
          initialValues[schema.id] = fieldValue?.metadata?.value?.[0] || '';
        } else {
          initialValues[schema.id] = fieldValue?.value || '';
        }
      });
      setFormValues(initialValues);
    }
  }, [stageTaskDetails]);

  const handleInputChange = (fieldId: number, value: any) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async () => {
    try {
      // Transform form values to the expected format
      const fields = Object.entries(formValues).map(([fieldId, value]) => {
        const schema = stageTaskDetails?.fieldSchemas?.find((s) => s.id === parseInt(fieldId, 10));

        if (schema?.type === FieldSchemaType.MULTI_SELECT || schema?.type === FieldSchemaType.SINGLE_SELECT) {
          // For select fields, put values in metadata
          return {
            fieldSchemaId: parseInt(fieldId, 10),
            value: null,
            metadata: {
              value: Array.isArray(value) ? value : [value]
            }
          };
        } else {
          // For text/date fields, put in value
          return {
            fieldSchemaId: parseInt(fieldId, 10),
            value: value || null,
            metadata: null
          };
        }
      });

      await updateStageTaskDetails({
        data: {
          programId,
          stageId,
          memberId,
          fields
        }
      }, refetchData);

      // Close drawer on successful submission
      onClose();
    } catch (error) {
      console.error('Failed to update stage task details:', error);
      // Handle error (you might want to show a toast notification)
    }
  };

  if (loading) {
    // Add a loading state
    return <div />;
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[40vw] min-w-[480px] max-w-[720px] p-0 flex flex-col [&>button:first-of-type]:hidden">
        {/* Header */}
        <div className="self-stretch px-6 py-4 border-b border-gray-200 inline-flex justify-center items-center gap-2">
          <div className="flex-1 justify-center text-zinc-900 text-base font-medium font-['Inter'] leading-7">
            {stageTaskDetails?.stage?.name}
          </div>
          <div className="flex justify-start items-center">
            <button
              onClick={onClose}
              className="w-4 h-4 flex items-center justify-center text-gray-800 hover:text-gray-600 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Member Info */}
        <div className="px-4 pb-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <NameCell
              imageUrl={member.profilePicture}
              name={member.name}
              showName={false}
            />
            {/* <Avatar src={member.profilePicture} fallback={member.name[0]} className="h-8 w-8" /> */}
            <span className="text-sm font-medium text-gray-800">{member.name}</span>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 flex flex-col gap-4">
          <div className="p-4 bg-white rounded-2xl border border-gray-200">
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-800">
                {stageTaskDetails?.name || ''}
                {/* Please refer to our content best <a href="#" className="text-blue-600 underline">practices</a>. */}
              </p>

              {/* Dynamic Form Fields */}
              <div className="space-y-4">
                {stageTaskDetails?.fieldSchemas?.map((schema) => {
                  if (!schema) return null;

                  // Handle Upload type field
                  if (schema.type === FieldSchemaType.UPLOAD) {
                    return (
                      <div key={schema.id} className="space-y-2">
                        <div className="flex items-center gap-1">
                          <div className="text-zinc-900 text-xs font-medium">
                            {schema.name}<span className="text-red-400">*</span>
                          </div>
                          <CircleInfoIcon className="h-3 w-3 text-gray-300" />
                        </div>
                        <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-center">
                          <Button variant="outline" className="mb-2 h-8">
                            <ArrowUpFromLineIcon className="h-3.5 w-3.5 mr-2" />
                            Upload File
                          </Button>
                          <p className="text-sm text-gray-800">Select a file to upload</p>
                        </div>
                      </div>
                    );
                  }

                  // Handle other field types
                  const fieldValue = formValues[schema.id];

                  return (
                    <div key={schema.id} className="relative h-12 rounded-lg border border-gray-200">
                      <label
                        className={`absolute left-3 transition-all duration-200 pointer-events-none
                          ${fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue)
                            ? 'text-xs text-gray-500 top-1'
                            : 'text-sm text-gray-500 top-3'}`}
                      >
                        {schema.name}<span className="text-red-400">*</span>
                      </label>

                      {schema.type === FieldSchemaType.MULTI_SELECT ? (
                        <MultiSelect
                          options={schema.metaData?.options || []}
                          value={Array.isArray(fieldValue) ? fieldValue : []}
                          onChange={(value) => handleInputChange(schema.id, value)}
                          placeholder=""
                        />
                      ) : schema.type === FieldSchemaType.SINGLE_SELECT ? (
                        <SingleSelect
                          options={schema.metaData?.options || []}
                          value={fieldValue || ''}
                          onChange={(value) => handleInputChange(schema.id, value)}
                          placeholder=""
                        />
                      ) : schema.type === FieldSchemaType.BOOLEAN ? (
                        <SingleSelect
                          options={['Yes', 'No']}
                          value={fieldValue === 'true' ? 'Yes' : fieldValue === 'false' ? 'No' : ''}
                          onChange={(value) => handleInputChange(schema.id, value === 'Yes' ? 'true' : 'false')}
                          placeholder=""
                        />
                      ) : (
                        <div className="relative w-full h-full">
                          <input
                            type={schema.type === FieldSchemaType.DATE ? 'date' : 'text'}
                            value={fieldValue}
                            onChange={(e) => handleInputChange(schema.id, e.target.value)}
                            className={`w-full h-full bg-transparent border-0 focus:ring-0 focus:outline-none
                              ${fieldValue ? 'pt-5 pb-1' : 'px-3'} text-gray-800 text-sm
                              ${schema.type === FieldSchemaType.DATE ? 'pr-8' : ''}`}
                            style={{
                              paddingLeft: '12px',
                              ...(schema.type === FieldSchemaType.DATE && {
                                WebkitAppearance: 'none',
                                MozAppearance: 'textfield'
                              })
                            }}
                          />
                          {schema.type === FieldSchemaType.DATE && (
                            <div
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer"
                              onClick={() => {
                                const input = document.querySelector('input[type="date"]') as HTMLInputElement;
                                if (input) {
                                  input.focus();
                                  input.showPicker?.();
                                }
                              }}
                            >
                              <div className="flex items-center justify-center">
                                <div className="w-4 h-4 relative">
                                  <svg
                                    className="w-4 h-4 absolute left-[1px] top-0 text-zinc-900"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          )}
                          {schema.type === FieldSchemaType.DATE && (
                            <style jsx>{`
                              input[type="date"]::-webkit-calendar-picker-indicator {
                                opacity: 0;
                                position: absolute;
                                right: 0;
                                width: 100%;
                                height: 100%;
                                cursor: pointer;
                              }
                              input[type="date"]::-webkit-inner-spin-button,
                              input[type="date"]::-webkit-outer-spin-button {
                                -webkit-appearance: none;
                                margin: 0;
                              }
                            `}ts-no
                            </style>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-auto px-6 py-4 border-t border-gray-200 bg-white">
          <div className="flex gap-2">
            <Button
              onClick={handleSubmit}
              disabled={!Object.keys(formValues).length || updateLoading}
              className="h-8 px-3 py-1 bg-gray-800 rounded-lg text-white text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            >
              {updateLoading ? 'Saving...' : 'Submit'}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={updateLoading}
              className="h-8 px-3 py-1 bg-white rounded-lg border border-gray-200 text-gray-800 text-sm font-medium"
            >
              Cancel
            </Button>
          </div>
          {updateError && (
            <p className="text-red-500 text-sm mt-2">
              Failed to save changes. Please try again.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
